import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { signinUser } from "../features/auth/authSlice";
import {
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  InputAdornment,
  Divider,
  Link,
  Stack,
  Paper,
  useTheme,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import LoginIcon from "@mui/icons-material/Login";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import { useNavigate, useLocation } from "react-router-dom";
import { BACKEND_URL } from "../config";

const GoogleGIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M23.49,12.27c0-.82-.07-1.61-.21-2.38H12v4.51h6.44c-.28,1.51-1.13,2.78-2.4,3.64v3.02h3.89c2.28-2.1,3.59-5.18,3.59-8.79Z"
    />
    <path
      fill="#34A853"
      d="M12,24c3.24,0,5.96-1.07,7.94-2.91l-3.89-3.02c-1.08,.73-2.46,1.16-4.05,1.16-3.11,0-5.75-2.1-6.7-4.92H1.38v3.12c1.98,3.92,6.01,6.67,10.62,6.67Z"
    />
    <path
      fill="#FBBC05"
      d="M5.3,14.31c-.24-.73-.38-1.5-.38-2.31s.14-1.58,.38-2.31V6.57H1.38c-.85,1.72-1.34,3.67-1.34,5.73s.49,4.01,1.34,5.73l3.92-3.12Z"
    />
    <path
      fill="#EA4335"
      d="M12,4.81c1.76,0,3.35,.61,4.59,1.79l3.44-3.44C17.96,1.19,15.24,0,12,0,7.38,0,3.35,2.75,1.38,6.57l3.92,3.12c.95-2.82,3.59-4.88,6.7-4.88Z"
    />
  </svg>
);

function SignIn({ onClose }) {
  const dispatch = useDispatch();
  const { loading, error } = useSelector((state) => state.auth);
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const location = useLocation();

  const [form, setForm] = useState({ username: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [oauthError, setOauthError] = useState(null);

  const colors = {
    primary: "#e50914",
    secondary: "#6a0dad",
    accent: "#A380FF",
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const errorMsg = params.get("error");
    if (errorMsg) {
      setOauthError(decodeURIComponent(errorMsg.replace(/\+/g, " ")));
    }
  }, [location]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(signinUser(form)).then((res) => {
      if (res.meta.requestStatus === "fulfilled") {
        if (onClose) {
          setTimeout(onClose, 50);
        } else {
          // If not in a modal, redirect to original page or home
          const from = location.state?.from?.pathname || "/";
          navigate(from, { replace: true });
        }
      }
    });
  };

  const handleGoogleSignIn = () => {
    const returnTo = location.state?.from?.pathname || window.location.pathname;
    // Don't redirect back to signin or signup
    const finalReturnTo = (returnTo === "/signin" || returnTo === "/signup") ? "/" : returnTo;
    window.location.href = `${BACKEND_URL}/auth/google/login?return_to=${encodeURIComponent(finalReturnTo)}`;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        position: "relative",
        p: { xs: 3, sm: 4, md: 5 },
        maxWidth: 450,
        mx: "auto",
        background: isDark
          ? "linear-gradient(135deg, rgba(26, 26, 26, 0.95) 0%, rgba(30, 30, 30, 0.95) 100%)"
          : "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(250, 250, 250, 0.95) 100%)",
        backdropFilter: "blur(10px)",
        borderRadius: "24px",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
      }}
    >
      {/* Close button */}
      {onClose && (
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            color: theme.palette.text.secondary,
            "&:hover": { color: colors.primary },
          }}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      )}

      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 64,
            height: 64,
            borderRadius: "50%",
            background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
            mb: 2,
          }}
        >
          <LoginIcon sx={{ fontSize: 32, color: "#fff" }} />
        </Box>
        <Typography
          variant="h4"
          fontWeight={900}
          sx={{
            mb: 1,
            background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Welcome Back
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Sign in to continue your learning journey
        </Typography>
      </Box>

      {(error || oauthError) && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: "12px",
            "& .MuiAlert-icon": { color: colors.primary },
          }}
        >
          {typeof error === "string"
            ? error
            : error?.detail || oauthError || "Something went wrong. Please try again."}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Stack spacing={2.5}>
          {/* Username */}
          <TextField
            label="Username"
            name="username"
            type="text"
            fullWidth
            value={form.username}
            onChange={handleChange}
            required
            autoComplete="username"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon sx={{ color: colors.primary }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                "&:hover fieldset": {
                  borderColor: colors.primary,
                },
              },
            }}
          />

          {/* Password */}
          <TextField
            label="Password"
            name="password"
            type={showPassword ? "text" : "password"}
            fullWidth
            value={form.password}
            onChange={handleChange}
            required
            autoComplete="current-password"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ color: colors.secondary }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                "&:hover fieldset": {
                  borderColor: colors.secondary,
                },
              },
            }}
          />

          {/* Remember Me & Forgot Password */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  sx={{
                    color: colors.primary,
                    "&.Mui-checked": {
                      color: colors.primary,
                    },
                  }}
                />
              }
              label={
                <Typography variant="body2" color="text.secondary">
                  Remember me
                </Typography>
              }
            />
            <Link
              component="button"
              type="button"
              sx={{
                color: colors.primary,
                textDecoration: "none",
                fontSize: "0.875rem",
                fontWeight: 500,
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              Forgot password?
            </Link>
          </Box>

          {/* Sign In Button */}
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
            sx={{
              mt: 1,
              py: 1.5,
              borderRadius: "12px",
              fontSize: "1rem",
              fontWeight: 700,
              background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
              boxShadow: `0 4px 16px ${colors.primary}40`,
              transition: "all 0.3s ease",
              "&:hover": {
                background: `linear-gradient(135deg, #ff1a3c, #8e24aa)`,
                boxShadow: `0 8px 24px ${colors.primary}60`,
                transform: "translateY(-2px)",
              },
              "&:disabled": {
                background: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
              },
            }}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: "#fff" }} />
            ) : (
              "Sign In"
            )}
          </Button>
        </Stack>
      </form>

      {/* Divider */}
      <Box sx={{ my: 3, position: "relative" }}>
        <Divider>
          <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
            OR
          </Typography>
        </Divider>
      </Box>

      {/* Google Sign In */}
      <Button
        variant="outlined"
        fullWidth
        startIcon={<GoogleGIcon />}
        onClick={handleGoogleSignIn}
        sx={{
          py: 1.5,
          borderRadius: "12px",
          fontSize: "1rem",
          fontWeight: 600,
          borderColor: isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)",
          color: theme.palette.text.primary,
          transition: "all 0.3s ease",
          "&:hover": {
            borderColor: colors.primary,
            background: isDark ? "rgba(229, 9, 20, 0.1)" : "rgba(229, 9, 20, 0.05)",
            transform: "translateY(-2px)",
          },
        }}
      >
        Sign in with Google
      </Button>

      {/* Sign Up Link */}
      <Box sx={{ textAlign: "center", mt: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Don't have an account?{" "}
          <Link
            component="button"
            type="button"
            onClick={() => {
              if (onClose) onClose();
              navigate("/signup");
            }}
            sx={{
              color: colors.primary,
              fontWeight: 600,
              textDecoration: "none",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            Sign Up
          </Link>
        </Typography>
      </Box>
    </Paper>
  );
}

export default SignIn;
